import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Demanda, DemandaPrioridade, DemandaTipo, TIPO_LABELS, TIPO_OPTS } from './demandaTypes';

type CompanyOpt = { id: string; name: string };

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (d: Demanda) => void;
  defaultCompanyId?: string;
};

export function DemandaCreateModal({ open, onClose, onCreated, defaultCompanyId }: Props) {
  const [companies, setCompanies] = useState<CompanyOpt[]>([]);
  const [companyId, setCompanyId] = useState(defaultCompanyId || '');
  const [tipo, setTipo] = useState<DemandaTipo>('SUPORTE');
  const [prioridade, setPrioridade] = useState<DemandaPrioridade>('MEDIA');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      try {
        const res = await api.get('/companies');
        const list = (res.data?.data ?? res.data ?? []) as CompanyOpt[];
        setCompanies(Array.isArray(list) ? list : []);
        if (!companyId && list[0]?.id) setCompanyId(list[0].id);
      } catch {
        setCompanies([]);
      }
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (defaultCompanyId) setCompanyId(defaultCompanyId);
  }, [defaultCompanyId]);

  const submit = async () => {
    if (!companyId) {
      toast.error('Selecione a empresa');
      return;
    }
    if (titulo.trim().length < 3) {
      toast.error('Título muito curto');
      return;
    }
    if (descricao.trim().length < 10) {
      toast.error('Descrição deve ter pelo menos 10 caracteres');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/rique-atendimento/demandas', {
        companyId,
        tipo,
        prioridade,
        titulo: titulo.trim(),
        descricao: descricao.trim(),
      });
      const created = res.data?.data as Demanda;
      toast.success(`Demanda ${created.protocolo} criada`);
      setTitulo('');
      setDescricao('');
      setTipo('SUPORTE');
      setPrioridade('MEDIA');
      onCreated(created);
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Falha ao criar demanda');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Nova demanda" size="lg">
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium">Empresa</label>
          <Select value={companyId} onValueChange={setCompanyId}>
            <option value="">Selecione…</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium">Tipo</label>
            <Select value={tipo} onValueChange={(v) => setTipo(v as DemandaTipo)}>
              {TIPO_OPTS.map((t) => (
                <option key={t} value={t}>
                  {TIPO_LABELS[t]}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Prioridade</label>
            <Select
              value={prioridade}
              onValueChange={(v) => setPrioridade(v as DemandaPrioridade)}
            >
              <option value="BAIXA">Baixa</option>
              <option value="MEDIA">Média</option>
              <option value="ALTA">Alta</option>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Título</label>
          <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-medium">Descrição</label>
          <Textarea rows={5} value={descricao} onChange={(e) => setDescricao(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={() => void submit()} disabled={saving}>
            {saving ? 'Criando…' : 'Criar demanda'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
